import { getFulcrumPhotoAsBlob, FULCRUM_BASE_URLS, FulcrumRegion } from "./fulcrumLibService";
import { constructMediaUrl } from "./mediaUtils";
import { getDataSourceType } from "./dataSourceAdapters";
import { getThumbnailUrl, constructThumbnailUrl, constructCdnThumbnailUrl } from "./temporaryUrlUtils";
import { apiFetch, REACT_APP_API_ROOT } from "./api";
import { isEnvCdnUrl } from "./cdnUtils";

// ============================================================================
// Generalized Authentication-Based Media Adapter Interfaces
// ============================================================================

export type MediaResourceType = "thumbnail" | "preview" | "full";

export interface MediaResourceOptions {
  size?: { width: number; height: number };
  quality?: number;
  format?: "jpg" | "png" | "webp";
}

export interface MediaResourceResult {
  url: string;
  authMethod: "headers" | "cookies" | "direct" | "api";
  requiresBlobConversion: boolean;
  isTested: boolean;
  fallbackAvailable: boolean;
}

export interface AuthenticatedApiConfig {
  name: string;
  apiKey: string;
  baseUrl: string;
  headers: Record<string, string>;
  regionDetector?: (item: any) => string;
  photoUrlBuilder: (photoId: string, region: string, thumbnail: boolean) => string;
  photoBlobFetcher: (photoId: string, userProfile: any, region: string, thumbnail: boolean) => Promise<string>;
}

export type MediaResourceHandler = (
  item: any,
  resourceType: MediaResourceType,
  options?: MediaResourceOptions
) => Promise<MediaResourceResult>;

/**
 * Cache successful media URL for future use
 */
async function cacheMediaUrl(itemId: any, resourceType: MediaResourceType, url: string): Promise<void> {
  try {
    const { cacheData } = await import("./relatedItemsCache");
    const cacheKey = `media_${itemId}_${resourceType}`;
    await cacheData(cacheKey, "media", url);
    console.log(`[MediaAuth] Cached media URL for ${itemId} (${resourceType})`);
  } catch (error) {
    console.warn("[MediaAuth] Failed to cache media URL:", error);
  }
}

/**
 * Handle cookie-authenticated resources (CDN/Django)
 * Automatically fetches complete item details if needed
 */
async function getCookieAuthenticatedResource(
  item: any,
  resourceType: MediaResourceType,
  options: MediaResourceOptions = {}
): Promise<MediaResourceResult> {
  const result: MediaResourceResult = {
    url: "",
    authMethod: "cookies",
    requiresBlobConversion: false,
    isTested: false,
    fallbackAvailable: true
  };

  console.log(`[CookieAuthenticatedResource] Processing ${resourceType} for item:`, item.id);

  // Check cache for media URLs to avoid repeated fetches
  let cachedMediaUrl: string | null = null;
  try {
    const { getCachedData } = await import("./relatedItemsCache");
    const cacheKey = `media_${item.id}_${resourceType}`;
    cachedMediaUrl = getCachedData<string>(cacheKey, "media");

    if (cachedMediaUrl) {
      console.log(`[CookieAuthenticatedResource] Cache hit for media URL: ${item.id}`);
      result.url = cachedMediaUrl;
      result.isTested = true;
      return result;
    }
  } catch (cacheError) {
    console.warn("[CookieAuthenticatedResource] Cache check failed:", cacheError);
  }

  // STEP 1: Ensure we have complete item details for Django items
  let completeItem = item;
  const needsCompleteDetails =
    typeof item.id === "number" &&
    // full needs details when url missing
    ((resourceType === "full" && !item.url) ||
      // thumbnails for embeds often rely on server-provided thumbnail fields
      (resourceType === "thumbnail" &&
        (!item.thumbnail || item.media_type?.toLowerCase?.().startsWith("embed/") || item.media_type?.toLowerCase?.() === "embed")));

  if (needsCompleteDetails) {
    console.log(`[CookieAuthenticatedResource] Fetching complete Django item details for ${resourceType}...`);
    try {
      const fetchedItem = await apiFetch(`${REACT_APP_API_ROOT}api/v2/media/${item.id}/`);
      if (fetchedItem) {
        completeItem = fetchedItem;
        console.log(`[CookieAuthenticatedResource] ✅ Got complete Django item details:`, {
          id: completeItem.id,
          hasUrl: !!completeItem.url,
          hasThumbnail: !!completeItem.thumbnail
        });
      }
    } catch (error) {
      console.warn(`[CookieAuthenticatedResource] Failed to fetch complete details, using item as-is:`, error);
    }
  }

  // STEP 2: Process based on resource type using complete item
  if (resourceType === "thumbnail") {
    console.log(`[CookieAuthenticatedResource] Processing thumbnail for item ${completeItem.id}`);

    // Prefer explicit server-provided thumbnail fields (critical for embed items and non-image media),
    // rather than guessing `thumbnail.jpg` next to the full media URL.
    const serverThumbnailSources = [
      completeItem.entity_thumbnail,
      completeItem.thumbnail,
      completeItem.primary_image?.thumbnail,
      completeItem.remote_details?.thumbnail
    ].filter((url: any) => url && url !== null);

    for (const thumbnailUrl of serverThumbnailSources) {
      if (typeof thumbnailUrl === "string") {
        // Even if the URL doesn't end with an image extension (e.g. `/thumbnail/`),
        // the server may still serve an image content-type. Let the browser decide.
        result.url = constructMediaUrl(thumbnailUrl);
        result.authMethod = isEnvCdnUrl(result.url) ? "cookies" : "direct";
        result.requiresBlobConversion = false;
        result.isTested = false;
        console.log(`[CookieAuthenticatedResource] Using server-provided thumbnail: ${result.url}`);
        await cacheMediaUrl(item.id, resourceType, result.url);
        return result;
      }
    }

    // Fall back to temporaryUrlUtils which (a) tests CDN thumbnail.jpg existence and
    // (b) checks server thumbnail fields if CDN doesn't have one.
    try {
      const thumbnailResult = await getThumbnailUrl(completeItem);
      if (thumbnailResult.url) {
        result.url = thumbnailResult.url;
        result.isTested = thumbnailResult.tested;
        result.authMethod = thumbnailResult.source === "cdn" ? "cookies" : "direct";
        console.log(`[CookieAuthenticatedResource] Thumbnail resolved via temporaryUrlUtils (${thumbnailResult.source}): ${result.url}`);

        // Cache successful thumbnail URL
        await cacheMediaUrl(item.id, resourceType, result.url);
        return result;
      }
    } catch (error) {
      console.error("[CookieAuthenticatedResource] Error with temporaryUrlUtils:", error);
    }

    // Final fallback: use sync construction
    const syncThumbnailUrl = constructThumbnailUrl(completeItem);
    if (syncThumbnailUrl) {
      result.url = syncThumbnailUrl;
      result.authMethod = isEnvCdnUrl(syncThumbnailUrl) ? "cookies" : "direct";
      console.log(`[CookieAuthenticatedResource] Fallback thumbnail: ${result.url}`);
      return result;
    }
  } else if (resourceType === "full" || resourceType === "preview") {
    // Embeds (e.g. Polycam) should be rendered via `embed_url`, not `url`.
    // Returning thumbnail URLs here breaks the viewer (it expects an iframe).
    const completeMediaType = (completeItem?.media_type || completeItem?.type || "").toString().toLowerCase();
    const embedUrl = completeItem?.embed_url || completeItem?.remote_details?.embed_url;
    const isEmbed = completeMediaType.startsWith("embed/") || completeMediaType === "embed";

    if (isEmbed) {
      result.url = embedUrl || "";
      result.authMethod = "direct";
      result.requiresBlobConversion = false;
      result.isTested = true;
      result.fallbackAvailable = Boolean(embedUrl);
      console.log(`[CookieAuthenticatedResource] Using embed_url for ${resourceType}:`, {
        id: completeItem?.id,
        hasEmbedUrl: Boolean(embedUrl)
      });
      if (result.url) {
        await cacheMediaUrl(item.id, resourceType, result.url);
      }
      return result;
    }

    // For full images and previews, try multiple fallback sources
    const fullImageSources = [
      completeItem.url, // Primary URL
      completeItem.primary_image?.url, // Primary image URL
      completeItem.remote_details?.url, // Remote details URL
      completeItem.thumbnail // Sometimes thumbnail IS the full URL
    ];

    for (const sourceUrl of fullImageSources) {
      if (sourceUrl && sourceUrl !== null) {
        // If it's a CDN URL, use it directly with cookie auth
        if (isEnvCdnUrl(sourceUrl)) {
          result.url = constructMediaUrl(sourceUrl);
          result.authMethod = "cookies";
          result.requiresBlobConversion = false;
          console.log(`[CookieAuthenticatedResource] Using CDN URL for ${resourceType}: ${result.url}`);

          // Cache successful URL
          await cacheMediaUrl(item.id, resourceType, result.url);
          return result;
        }

        // For non-CDN URLs, use direct access
        result.url = constructMediaUrl(sourceUrl);
        result.authMethod = "direct";
        result.requiresBlobConversion = false;
        console.log(`[CookieAuthenticatedResource] Using direct URL for ${resourceType}: ${result.url}`);

        // Cache successful URL
        await cacheMediaUrl(item.id, resourceType, result.url);
        return result;
      }
    }

    // If no URL found but we have a CDN thumbnail, try to construct full URL from it
    if (completeItem.thumbnail && isEnvCdnUrl(completeItem.thumbnail)) {
      // Try constructing a full CDN URL from the thumbnail path
      const cdnFullUrl = completeItem.thumbnail.replace("/thumbnail.jpg", "");
      if (cdnFullUrl !== completeItem.thumbnail) {
        result.url = constructMediaUrl(cdnFullUrl);
        result.authMethod = "cookies";
        result.requiresBlobConversion = false;
        console.log(`[CookieAuthenticatedResource] Constructed ${resourceType} URL from thumbnail path: ${result.url}`);
        return result;
      }

      // Fallback: Use thumbnail as full URL (for cases where thumbnail IS the full image)
      result.url = constructMediaUrl(completeItem.thumbnail);
      result.authMethod = "cookies";
      result.requiresBlobConversion = false;
      console.log(`[CookieAuthenticatedResource] Using thumbnail URL as ${resourceType} URL: ${result.url}`);
      return result;
    }
  }

  // No URL found
  console.warn(`[CookieAuthenticatedResource] No ${resourceType} URL found for item:`, completeItem.id);
  result.fallbackAvailable = false;
  return result;
}

// ============================================================================
// Header-Authenticated Resources (Generic for any API requiring headers)
// ============================================================================

export const getHeaderAuthenticatedResource = async (
  item: any,
  resourceType: MediaResourceType,
  apiConfig: AuthenticatedApiConfig,
  userProfile: any,
  options: MediaResourceOptions = {}
): Promise<MediaResourceResult> => {
  const result: MediaResourceResult = {
    url: "",
    authMethod: "api",
    requiresBlobConversion: false,
    isTested: false,
    fallbackAvailable: false
  };

  // Check for photo data (generic approach) - avoid "thumbnail" string from bulk processing failures
  const photoId = item.fulcrum_photo?.photo_id || (item.primary_image?.id !== "thumbnail" ? item.primary_image?.id : null) || item.id;
  const formId = item.fulcrum_photo?.form_id || item.fulcrum_data?.form_id || item.form_id;

  if (photoId && (item.fulcrum_photo || item.primary_image)) {
    // Detect region using provided detector or default
    const region = apiConfig.regionDetector ? apiConfig.regionDetector(item) : "AU";

    try {
      switch (resourceType) {
        case "thumbnail":
          result.url = await apiConfig.photoBlobFetcher(photoId, userProfile, region.toUpperCase(), true);
          result.authMethod = "headers";
          result.requiresBlobConversion = true;
          result.fallbackAvailable = true;
          return result;

        case "preview":
          // For previews, use full size photo
          result.url = await apiConfig.photoBlobFetcher(photoId, userProfile, region.toUpperCase(), false);
          result.authMethod = "headers";
          result.requiresBlobConversion = true;
          result.fallbackAvailable = true;
          return result;

        case "full":
          // Full size photo
          result.url = await apiConfig.photoBlobFetcher(photoId, userProfile, region.toUpperCase(), false);
          result.authMethod = "headers";
          result.requiresBlobConversion = true;
          result.fallbackAvailable = true;
          return result;
      }
    } catch (error) {
      console.error(`[${apiConfig.name}Resource] ❌ Error fetching authenticated photo:`, error);

      // Check if this is a 403 Forbidden error (expired signature, permissions, etc.)
      const errorMessage = error instanceof Error ? error.message : String(error);
      const is403Error =
        errorMessage.includes("403") || errorMessage.includes("Forbidden") || errorMessage.toLowerCase().includes("forbidden");

      if (is403Error) {
        console.warn(`[${apiConfig.name}Resource] ⚠️ 403 Forbidden - trying original URL as fallback`);

        // Try to fall back to the original URL from the item
        const originalUrl = resourceType === "thumbnail" ? item.thumbnail : item.url;
        const fallbackUrl = originalUrl || item.url || item.thumbnail;

        if (fallbackUrl) {
          result.url = fallbackUrl;
          result.authMethod = "direct"; // Try direct access without authentication
          result.requiresBlobConversion = false;
          result.fallbackAvailable = true;
          result.isTested = true;
          return result;
        } else {
          console.warn(`[${apiConfig.name}Resource] ❌ No fallback URL available - all Fulcrum URLs require authentication`);
        }
      }

      // For other authenticated photo failures, return error result
      // (Fulcrum photos don't exist in Django and require private API keys)
      console.error(`[${apiConfig.name}Resource] ❌ Authentication failed for photo ${photoId}:`, error);

      // If this was a full-size request that failed, try falling back to thumbnail
      if (resourceType === "full" && item.thumbnail) {
        result.url = item.thumbnail;
        result.authMethod = "direct";
        result.requiresBlobConversion = false;
        result.fallbackAvailable = true;
        return result;
      }

      result.url = "";
      result.authMethod = "headers";
      result.requiresBlobConversion = true;
      result.fallbackAvailable = false;
      result.isTested = true;
      console.warn(`[${apiConfig.name}Resource] ⚠️ Authentication failed - photo requires private API key`);
      return result;
    }
  }

  // Only fall back to cookie-authenticated handler for non-photo items
  // (items that don't have photo IDs but might have regular URLs)
  return getCookieAuthenticatedResource(item, resourceType, options);
};

// ============================================================================
// Pre-configured API Configs (for convenience)
// ============================================================================

/**
 * Creates Fulcrum API config with API key from user profile only
 * @param userProfile User profile containing API key and region
 */
const createFulcrumApiConfig = (userProfile: any): AuthenticatedApiConfig => {
  // Get API key from user profile only - no environment variable fallback
  const apiKey = userProfile?.fulcrum_api_key || "";
  // Get region from user profile, default to AU if not set
  const region: FulcrumRegion = userProfile?.fulcrum_region || "AU";

  if (!apiKey) {
    console.warn("No Fulcrum API key available in user profile for media adapter");
  }

  // Use shared FULCRUM_BASE_URLS constant (DRY)
  const baseUrl = FULCRUM_BASE_URLS[region] || FULCRUM_BASE_URLS.AU;

  return {
    name: "Fulcrum",
    apiKey: apiKey,
    baseUrl: baseUrl,
    headers: {
      "X-ApiToken": apiKey,
      "Content-Type": "application/json"
    },
    regionDetector: () => region, // Use region from user profile instead of guessing
    photoUrlBuilder: (photoId: string, regionParam: string, thumbnail: boolean) => {
      // Use the region passed in, or fall back to user profile region
      const effectiveRegion = (regionParam || region) as FulcrumRegion;
      const photoBaseUrl = FULCRUM_BASE_URLS[effectiveRegion] || FULCRUM_BASE_URLS.AU;
      const suffix = thumbnail ? "/thumbnail.jpg" : ".jpg";
      return `${photoBaseUrl}/photos/${photoId}${suffix}`;
    },
    photoBlobFetcher: (photoId: string, userProfile: any, regionParam: string, thumbnail: boolean) =>
      getFulcrumPhotoAsBlob(photoId, userProfile, thumbnail)
  };
};

// ============================================================================
// Main Resource Router (Authentication-method based)
// ============================================================================

export const getMediaResource = async (
  item: any,
  resourceType: MediaResourceType,
  userProfile: any,
  options: MediaResourceOptions = {}
): Promise<MediaResourceResult> => {
  const dataSourceType = getDataSourceType(item);

  // Route based on authentication requirements, not vendor
  switch (dataSourceType) {
    case "fulcrum":
      // Use header-authenticated handler with secure Fulcrum config
      const fulcrumConfig = createFulcrumApiConfig(userProfile);
      return getHeaderAuthenticatedResource(item, resourceType, fulcrumConfig, userProfile, options);

    default:
      // Use cookie-authenticated handler (CDN + Django)
      return getCookieAuthenticatedResource(item, resourceType, options);
  }
};

// ============================================================================
// Convenience Methods (for backwards compatibility)
// ============================================================================

export const getMediaThumbnail = async (item: any, userProfile: any, options: MediaResourceOptions = {}): Promise<MediaResourceResult> => {
  return getMediaResource(item, "thumbnail", userProfile, options);
};

export const getMediaFull = async (item: any, userProfile: any, options: MediaResourceOptions = {}): Promise<MediaResourceResult> => {
  return getMediaResource(item, "full", userProfile, options);
};

export const getMediaThumbnailSync = (item: any): string => {
  if (!item) return "";

  const dataSourceType = getDataSourceType(item);

  // For sync operations, we can't do blob conversion, so fall back to URLs
  if (dataSourceType === "fulcrum") {
    console.warn("[MediaResourceRouter] ⚠️ Sync thumbnail requested for authentication-required item, may fail");
  }

  // Use existing sync logic
  const thumbnailUrl = constructThumbnailUrl(item);
  if (thumbnailUrl) {
    return thumbnailUrl;
  }

  return "";
};
